import type { DateTime } from 'luxon';
import React, { useContext, useMemo, useState } from 'react';
import { convertSelectionToDateTime } from '../Timeline.utils';
// import { useSelectionContainer } from '@air/react-drag-to-select';
import { useSelectionContainer } from './drag-select/useSelectionContainer';
import { TimelineContext } from './Timeline.Context';
import styles from './Timeline.Selection.module.scss';

type SelectionProps = {
  /** Returns start/end time of selection (assuming `Timeline.fromDateTime` is set ) */
  onSelectionEnd?: (params: {
    action: 'add' | 'remove';
    started_at: DateTime;
    ended_at: DateTime;
  }) => void;
  snapToMinutes?: number;
};

/**
 * Allow click-to-select across the Timeline
 * `@air/react-drag-to-select` is doing all the heavy lifting here
 * @note Consider the existing hooks, but discuss with @0rvar about preventDefault stuff.
 */
export const TimelineSelection = (props: SelectionProps) => {
  const { snapToMinutes = 1 } = props;
  // Tracks how the scrollable container is positioned relative to the viewport
  // Move to context if we need this in other scenarios
  const [relativeDistance, setRelativeDistance] = useState({ top: 0, left: 0 });
  // Tracks how the position & size of the selection box
  const [position, setPosition] = useState({ top: 0, height: 0, left: 0, width: 0 });
  const {
    fromDateTime,
    scrollAreaRef,
    orientation,
    pxPerMinute,
    isDragMode,
    isAltKeyHeld,
    setIsDragMode,
  } = useContext(TimelineContext);

  const { DragSelection } = useSelectionContainer({
    eventsElement: scrollAreaRef,
    shouldStartSelecting: eventTarget => {
      // Prevent dragging on inputs
      if (eventTarget instanceof HTMLElement && eventTarget.closest('[data-no-drag-select]')) {
        return false;
      }
      // Resizable timestamps identify as `data-timestamp-adjuster` to block selection
      const isAdjustHandle = (eventTarget as HTMLElement)?.closest('[data-timestamp-adjuster]');
      return !isDragMode && !isAdjustHandle;
    },
    onSelectionChange: selection => {
      if (!isDragMode) return;
      const { scrollTop, scrollLeft } = scrollAreaRef!;
      const top = selection.top + scrollTop - relativeDistance.top;
      const left = selection.left + scrollLeft - relativeDistance.left;
      setPosition({ top, height: selection.height, left, width: selection.width });
    },
    onSelectionStart: () => {
      const { top = 0, left = 0 } = scrollAreaRef?.getBoundingClientRect() || {};
      setRelativeDistance({ top, left });
      setIsDragMode(true);
    },
    onSelectionEnd: () => {
      setIsDragMode(false);

      if (position.width > pxPerMinute || position.height > pxPerMinute) {
        const { started_at, ended_at } = convertSelectionToDateTime({
          position,
          orientation,
          pxPerMinute,
          fromDateTime,
          snapToMinutes,
        });
        if (started_at !== ended_at) {
          props.onSelectionEnd?.({ started_at, ended_at, action: isAltKeyHeld ? 'remove' : 'add' });
        }
      }
      setPosition({ top: 0, height: 0, left: 0, width: 0 });
    },
    // Don't actually render the selection box, we'll draw our own UI
    selectionProps: { style: { display: 'none' } },
  });

  const style = useMemo((): React.CSSProperties => {
    if (!isDragMode) return {};
    function snap(px: number) {
      return Math.round(px / pxPerMinute / snapToMinutes) * snapToMinutes * pxPerMinute;
    }
    if (orientation === 'vertical') {
      const top = position.top;
      const topSnapped = snap(top);
      const heightAdjustment = topSnapped - top;
      const height = snap(position.height - heightAdjustment);
      return { top: topSnapped, height, left: 0, right: 0 };
    }
    const left = position.left;
    const leftSnapped = snap(left);
    const widthAdjustment = leftSnapped - left;
    const width = snap(position.width - widthAdjustment);
    return { top: 0, bottom: 0, left: leftSnapped, width };
  }, [isDragMode, orientation, position, pxPerMinute, snapToMinutes]);

  return (
    <>
      <DragSelection />
      {isDragMode && <div className={styles.selectedArea} style={style} />}
    </>
  );
};
TimelineSelection.displayName = 'Timeline.Selection';
